import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { showToast } from '../lib/toast';
import { useConfirm } from '../hooks/useConfirm';
import DataTable from '../components/DataTable';
import clsx from 'clsx';

// Mock data for demo mode
const mockTools = [
  { id: 1, name: 'Google Classroom', domain_pattern: 'classroom.google.com', category_name: 'LMS', category_ids: '1', vendor: 'Google', visits: 12450, devices: 2340, is_approved: true },
  { id: 2, name: 'Canvas', domain_pattern: '*.instructure.com', category_name: 'LMS', category_ids: '1', vendor: 'Instructure', visits: 8920, devices: 1890, is_approved: true },
  { id: 3, name: 'Kahoot!', domain_pattern: 'kahoot.com', category_name: 'Assessment, Games', category_ids: '2,12', vendor: 'Kahoot', visits: 6340, devices: 1560, is_approved: true },
  { id: 4, name: 'Desmos', domain_pattern: 'desmos.com', category_name: 'Math, STEM', category_ids: '3,5', vendor: 'Desmos', visits: 5120, devices: 890, is_approved: true },
  { id: 5, name: 'Quizizz', domain_pattern: 'quizizz.com', category_name: 'Assessment', category_ids: '2', vendor: 'Quizizz', visits: 4890, devices: 1120, is_approved: true },
  { id: 6, name: 'Khan Academy', domain_pattern: 'khanacademy.org', category_name: 'Math, STEM', category_ids: '3,5', vendor: 'Khan Academy', visits: 4210, devices: 780, is_approved: true },
  { id: 7, name: 'Newsela', domain_pattern: 'newsela.com', category_name: 'Reading', category_ids: '4', vendor: 'Newsela', visits: 3890, devices: 920, is_approved: true },
  { id: 8, name: 'Blooket', domain_pattern: 'blooket.com', category_name: 'Assessment, Games', category_ids: '2,12', vendor: 'Blooket', visits: 3450, devices: 890, is_approved: false },
  { id: 9, name: 'Gimkit', domain_pattern: 'gimkit.com', category_name: 'Assessment, Games', category_ids: '2,12', vendor: 'Gimkit', visits: 2890, devices: 670, is_approved: false },
  { id: 10, name: 'Nearpod', domain_pattern: 'nearpod.com', category_name: 'Assessment, LMS', category_ids: '2,1', vendor: 'Nearpod', visits: 2340, devices: 450, is_approved: true },
];

const mockCategories = [
  { id: 1, name: 'LMS', description: 'Learning Management Systems', color: '#3b82f6', tool_count: 12 },
  { id: 2, name: 'Assessment', description: 'Testing and quiz tools', color: '#8b5cf6', tool_count: 18 },
  { id: 3, name: 'Math', description: 'Mathematics learning tools', color: '#06b6d4', tool_count: 8 },
  { id: 4, name: 'Reading', description: 'Reading tools', color: '#22c55e', tool_count: 5 },
  { id: 5, name: 'STEM', description: 'Science and Tech', color: '#f59e0b', tool_count: 3 },
  { id: 6, name: 'Productivity', description: 'General productivity', color: '#64748b', tool_count: 15 },
];

const mockPendingTools = [
  { id: 1, domain: 'edpuzzle.com', visit_count: 234, unique_devices: 45, first_seen_at: '2024-01-10' },
  { id: 2, domain: 'padlet.com', visit_count: 189, unique_devices: 32, first_seen_at: '2024-01-08' },
];

export default function Tools() {
  const { confirm, ConfirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState('inventory');

  // Inventory State
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [approvalFilter, setApprovalFilter] = useState('All');
  const [showAddToolModal, setShowAddToolModal] = useState(false);
  const [showEditToolModal, setShowEditToolModal] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [newTool, setNewTool] = useState({ name: '', domainPattern: '', categoryIds: [], vendor: '' });
  const [editTool, setEditTool] = useState(null);

  // Categories State
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [editCategoryModal, setEditCategoryModal] = useState(null);
  const [deleteCategoryModal, setDeleteCategoryModal] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#22c55e' });
  const [reassignCategory, setReassignCategory] = useState('');

  // Pending Review State
  const [approveModal, setApproveModal] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState(new Set());
  const [pendingPage, setPendingPage] = useState(1);
  const PENDING_PAGE_SIZE = 20;
  const [pendingSettings, setPendingSettings] = useState({});

  const queryClient = useQueryClient();

  // ==========================================
  // QUERIES
  // ==========================================

  const { data: toolsResponse, isLoading: toolsLoading } = useQuery({
    queryKey: ['tools', { category: categoryFilter, search: searchQuery, page, limit }],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (categoryFilter !== 'All') params.set('category', categoryFilter);
        if (searchQuery) params.set('search', searchQuery);
        params.set('limit', limit);
        params.set('offset', (page - 1) * limit);

        const response = await api.get(`/tools?${params}`);
        return response;
      } catch (e) {
        return { tools: mockTools };
      }
    },
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try {
        const response = await api.get('/tools/categories/list');
        return response;
      } catch (e) {
        return { categories: mockCategories };
      }
    },
  });

  // Pending tools (for review)
  const { data: pendingTools = [] } = useQuery({
    queryKey: ['pendingTools'],
    queryFn: async () => {
      const response = await api.get('/tools/pending/list?status=pending');
      return response.domains || response.data?.domains || [];
    },
    staleTime: 0, // Always fetch fresh data
  });

  // Ignored domains
  const { data: ignoredDomains = [] } = useQuery({
    queryKey: ['ignoredDomains'],
    queryFn: async () => {
      const response = await api.get('/tools/pending/list?status=ignored');
      return response.domains || response.data?.domains || [];
    },
  });

  // ==========================================
  // MUTATIONS
  // ==========================================

  // Tool Mutations
  const addToolMutation = useMutation({
    mutationFn: async (toolData) => {
      try {
        const result = await api.post('/tools', toolData);
        showToast.success(`Product "${toolData.name}" added successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to add product');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
      setShowAddToolModal(false);
      setNewTool({ name: '', domainPattern: '', categoryIds: [], vendor: '' });
    },
  });

  // Ignore domain mutation
  const ignoreDomainMutation = useMutation({
    mutationFn: async (domain) => {
      const result = await api.post(`/tools/pending/${encodeURIComponent(domain)}/ignore`);
      showToast.success('Domain ignored successfully');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingTools']);
      queryClient.invalidateQueries(['ignoredDomains']);
    },
    onError: (error) => {
      showToast.error(error.response?.data?.error || 'Failed to ignore domain');
    },
  });

  // Unignore domain mutation
  const unignoreDomainMutation = useMutation({
    mutationFn: async (domain) => {
      const result = await api.post(`/tools/pending/${encodeURIComponent(domain)}/unignore`);
      showToast.success('Domain moved back to pending');
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingTools']);
      queryClient.invalidateQueries(['ignoredDomains']);
    },
    onError: (error) => {
      showToast.error(error.response?.data?.error || 'Failed to unignore domain');
    },
  });

  const updateToolMutation = useMutation({
    mutationFn: async ({ id, ...toolData }) => {
      try {
        const result = await api.put(`/tools/${id}`, toolData);
        showToast.success('Product updated successfully');
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update product');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
      setShowEditToolModal(false);
      setEditTool(null);
    },
  });

  const deleteToolMutation = useMutation({
    mutationFn: async ({ id, name }) => {
      const confirmed = await confirm({
        title: 'Delete Product',
        message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
        confirmText: 'Delete Product',
        confirmStyle: 'danger'
      });

      if (!confirmed) throw new Error('Cancelled');

      try {
        const result = await api.delete(`/tools/${id}`);
        showToast.success(`Product "${name}" deleted successfully`);
        return result;
      } catch (error) {
        if (error.message !== 'Cancelled') {
          showToast.error(error.message || 'Failed to delete product');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['tools']);
    },
  });

  // Category Mutations
  const createCategoryMutation = useMutation({
    mutationFn: async (data) => {
      try {
        const result = await api.post('/tools/categories', data);
        showToast.success(`Category "${data.name}" created successfully`);
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to create category');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setShowAddCategoryModal(false);
      setNewCategory({ name: '', description: '', color: '#22c55e' });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, ...data }) => {
      try {
        const result = await api.put(`/tools/categories/${id}`, data);
        showToast.success('Category updated successfully');
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to update category');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setEditCategoryModal(null);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async ({ id, reassignTo, name }) => {
      try {
        const result = await api.delete(`/tools/categories/${id}${reassignTo ? `?reassignTo=${reassignTo}` : ''}`);
        showToast.success(`Category "${name}" deleted successfully`);
        return result;
      } catch (error) {
        if (!error.message?.includes('has tools assigned')) {
          showToast.error(error.message || 'Failed to delete category');
        }
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      queryClient.invalidateQueries(['tools']);
      setDeleteCategoryModal(null);
      setReassignCategory('');
    },
    onError: (error) => {
      if (error.message?.includes('has tools assigned')) {
        // Keep modal open for reassignment
      }
    },
  });

  // Pending Domain Mutations
  const approveMutation = useMutation({
    mutationFn: async ({ domain, name, categoryIds, vendor, isApproved }) => {
      try {
        const result = await api.post(`/tools/pending/${encodeURIComponent(domain)}/approve`, { name, categoryIds, vendor, isApproved });
        if (isApproved) {
          showToast.success(`Product "${name}" approved successfully`);
        } else {
          showToast.success(`Domain "${domain}" rejected`);
        }
        return result;
      } catch (error) {
        showToast.error(error.message || 'Failed to process approval');
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingDomains']);
      queryClient.invalidateQueries(['tools']);
      setApproveModal(null);
    },
  });

  // ==========================================
  // PENDING REVIEW HELPERS
  // ==========================================

  const paginatedPending = pendingTools.slice(
    (pendingPage - 1) * PENDING_PAGE_SIZE,
    pendingPage * PENDING_PAGE_SIZE
  );
  const totalPendingPages = Math.ceil(pendingTools.length / PENDING_PAGE_SIZE);

  const toggleDomain = (domain) => {
    setSelectedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  const toggleAllDomains = () => {
    if (selectedDomains.size === paginatedPending.length) {
      setSelectedDomains(new Set());
    } else {
      setSelectedDomains(new Set(paginatedPending.map(p => p.domain)));
    }
  };

  const getPendingSetting = (domain) => {
    return pendingSettings[domain] || { name: '', domainPattern: domain, categoryIds: [], wildcard: false };
  };

  const updatePendingSetting = (domain, field, value) => {
    setPendingSettings(prev => ({
      ...prev,
      [domain]: { ...getPendingSetting(domain), [field]: value }
    }));
  };

  const bulkApproveMutation = useMutation({
    mutationFn: async ({ domains, isApproved }) => {
      const results = await Promise.allSettled(
        domains.map(domain => {
          const s = getPendingSetting(domain);
          const pattern = s.wildcard ? `*.${domain}` : (s.domainPattern || domain);
          return api.post(`/tools/pending/${encodeURIComponent(domain)}/approve`, {
            name: s.name || domain,
            domainPattern: pattern,
            categoryIds: (s.categoryIds || []).map(id => parseInt(id)),
            vendor: '',
            isApproved
          });
        })
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      if (isApproved) {
        showToast.success(`${succeeded} product${succeeded !== 1 ? 's' : ''} approved${failed ? `, ${failed} failed` : ''}`);
      } else {
        showToast.success(`${succeeded} domain${succeeded !== 1 ? 's' : ''} denied${failed ? `, ${failed} failed` : ''}`);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingTools']);
      queryClient.invalidateQueries(['tools']);
      setSelectedDomains(new Set());
      setPendingSettings({});
    },
  });

  const bulkIgnoreMutation = useMutation({
    mutationFn: async (domains) => {
      const results = await Promise.allSettled(
        domains.map(domain => api.post(`/tools/pending/${encodeURIComponent(domain)}/ignore`))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      showToast.success(`${succeeded} domain${succeeded !== 1 ? 's' : ''} ignored`);
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['pendingTools']);
      queryClient.invalidateQueries(['ignoredDomains']);
      setSelectedDomains(new Set());
    },
  });

  // ==========================================
  // DATA PREP
  // ==========================================

  const toolsData = toolsResponse?.tools || (toolsLoading ? [] : mockTools);
  const stats = toolsResponse?.stats || (toolsLoading ? {
    total: 0,
    approved: 0,
    unapproved: 0,
    categories: 0
  } : {
    total: toolsData.length,
    approved: toolsData.filter(t => t.is_approved).length,
    unapproved: toolsData.filter(t => !t.is_approved).length,
    categories: new Set(toolsData.map(t => t.category_name).filter(Boolean)).size
  });
  const categories = categoriesResponse?.categories || mockCategories;

  const filteredTools = toolsData.filter(tool => {
    const matchesApproval = approvalFilter === 'All' ||
      (approvalFilter === 'Approved' && tool.is_approved) ||
      (approvalFilter === 'Unapproved' && !tool.is_approved);
    return matchesApproval;
  });

  const tabs = [
    { id: 'inventory', label: 'Inventory' },
    { id: 'pending', label: 'Review', count: pendingTools.length },
    { id: 'ignored', label: 'Ignored', count: ignoredDomains.length },
    { id: 'categories', label: 'Categories' },
  ];

  // ==========================================
  // HANDLERS
  // ==========================================

  const handleAddTool = (e) => {
    e.preventDefault();
    addToolMutation.mutate({
      name: newTool.name,
      domainPattern: newTool.domainPattern,
      categoryIds: newTool.categoryIds.map(id => parseInt(id)),
      vendor: newTool.vendor,
      isApproved: true
    });
  };

  const handleUpdateTool = (e) => {
    e.preventDefault();
    updateToolMutation.mutate({
      id: editTool.id,
      name: editTool.name,
      domainPattern: editTool.domainPattern,
      categoryIds: (editTool.categoryIds || []).map(id => parseInt(id)),
      vendor: editTool.vendor,
      isApproved: editTool.isApproved
    });
  };

  const handleCreateCategory = (e) => {
    e.preventDefault();
    createCategoryMutation.mutate(newCategory);
  };

  const handleUpdateCategory = (e) => {
    e.preventDefault();
    updateCategoryMutation.mutate({
      id: editCategoryModal.id,
      name: editCategoryModal.name,
      description: editCategoryModal.description,
      color: editCategoryModal.color,
    });
  };

  const handleDeleteCategory = () => {
    if (deleteCategoryModal.tool_count > 0 && !reassignCategory) {
      return;
    }
    deleteCategoryMutation.mutate({
      id: deleteCategoryModal.id,
      reassignTo: reassignCategory || undefined,
    });
  };

  const handleApprove = (e) => {
    e.preventDefault();
    approveMutation.mutate({
      domain: approveModal.domain,
      domainPattern: newTool.domainPattern,
      name: newTool.name,
      categoryIds: (newTool.categoryIds || []).map(id => parseInt(id)),
      vendor: newTool.vendor,
      isApproved: approveModal.isApproved,
    });
  };

  // ==========================================
  // COLUMNS
  // ==========================================

  const toolColumns = [
    {
      key: 'name',
      label: 'Product',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-surface-100 dark:bg-surface-700 rounded-lg flex items-center justify-center text-sm font-medium text-slate-700 dark:text-slate-200">
            {value?.[0] || '?'}
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-white">{value}</p>
            <p className="text-xs text-slate-500 font-mono">{row.domain_pattern}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category_name',
      label: 'Category',
      sortable: true,
      render: (value) => (
        <span className="px-2 py-1 bg-surface-100 dark:bg-surface-700 rounded text-xs font-medium text-slate-600 dark:text-slate-300">
          {value || 'Uncategorized'}
        </span>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
    },
    {
      key: 'visits',
      label: 'Visits (30d)',
      sortable: true,
      render: (value) => (value || 0).toLocaleString(),
    },
    {
      key: 'devices',
      label: 'Devices',
      sortable: true,
      render: (value) => (value || 0).toLocaleString(),
    },
    {
      key: 'is_approved',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={clsx(
          'px-2 py-1 rounded-full text-xs font-medium',
          value
            ? 'bg-brand-500/20 text-brand-400'
            : 'bg-amber-500/20 text-amber-400'
        )}>
          {value ? 'Approved' : 'Unapproved'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditTool({
                id: row.id,
                name: row.name,
                domainPattern: row.domain_pattern,
                categoryIds: row.category_ids ? row.category_ids.split(',') : [],
                vendor: row.vendor,
                isApproved: row.is_approved
              });
              setShowEditToolModal(true);
            }}
            className="p-1 hover:bg-surface-100 dark:hover:bg-surface-700 rounded text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
            title="Edit product"
          >
            <Pencil size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteToolMutation.mutate({ id: row.id, name: row.name });
            }}
            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
            disabled={deleteToolMutation.isPending}
            title="Delete product"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const categoryColumns = [
    {
      key: 'color',
      label: '',
      render: (value) => (
        <div
          className="w-4 h-4 rounded-full"
          style={{ backgroundColor: value || '#64748b' }}
        />
      ),
    },
    {
      key: 'name',
      label: 'Category',
      render: (value) => <span className="font-medium text-slate-900 dark:text-white">{value}</span>,
    },
    {
      key: 'description',
      label: 'Description',
    },
    {
      key: 'tool_count',
      label: 'Products',
      sortable: true,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            className="px-3 py-1 bg-surface-100 dark:bg-surface-600 text-slate-600 dark:text-slate-300 rounded text-sm hover:bg-surface-200 dark:hover:bg-surface-500"
            onClick={(e) => {
              e.stopPropagation();
              setEditCategoryModal(row);
            }}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded text-sm hover:bg-red-100 dark:hover:bg-red-500/30"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteCategoryModal(row);
            }}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Product Management</h1>
          <p className="text-slate-500 dark:text-slate-400">Manage your edtech inventory, categories, and new discoveries</p>
        </div>
        {activeTab === 'inventory' && (
          <button
            className="btn btn-primary"
            onClick={() => setShowAddToolModal(true)}
          >
            + Add Product
          </button>
        )}
        {activeTab === 'categories' && (
          <button
            onClick={() => setShowAddCategoryModal(true)}
            className="btn btn-primary"
          >
            + Add Category
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-surface-100 dark:bg-surface-800 rounded-lg w-fit border border-surface-200 dark:border-surface-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-white dark:bg-surface-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-2 px-2 py-0.5 bg-surface-200 dark:bg-surface-600 rounded-full text-xs text-slate-600 dark:text-slate-300">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Products</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">{stats.total}</p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Approved</p>
              <p className="text-2xl font-semibold text-brand-600 dark:text-brand-400">
                {stats.approved}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Unapproved</p>
              <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
                {stats.unapproved}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Categories</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-white">
                {stats.categories}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="card p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="input"
                />
              </div>

              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                className="input w-auto"
              >
                <option value="All">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>

              <select
                value={approvalFilter}
                onChange={(e) => { setApprovalFilter(e.target.value); setPage(1); }}
                className="input w-auto"
              >
                <option value="All">All Status</option>
                <option value="Approved">Approved</option>
                <option value="Unapproved">Unapproved</option>
              </select>
            </div>
          </div>

          {/* Tools Table */}
          <div className="space-y-4">
            <DataTable
              columns={toolColumns}
              data={filteredTools}
              emptyMessage="No products found matching your filters"
            />

            {/* Pagination Controls */}
            <div className="flex justify-between items-center px-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-slate-400">
                Page {page}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={toolsData.length < limit}
                className="btn btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          <DataTable
            columns={categoryColumns}
            data={categories}
            emptyMessage="No categories found"
          />
        </div>
      )}

      {/* Pending Review Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingTools.length > 0 && (
            <div className="card p-4 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20">
              <p className="text-amber-700 dark:text-amber-300 text-sm">
                <strong>Review Required:</strong> {pendingTools.length} new domain{pendingTools.length !== 1 ? 's' : ''} discovered.
                {selectedDomains.size > 0 && <span className="ml-2 font-semibold">{selectedDomains.size} selected</span>}
              </p>
            </div>
          )}

          {paginatedPending.length > 0 ? (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <th className="p-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={selectedDomains.size === paginatedPending.length && paginatedPending.length > 0}
                          onChange={toggleAllDomains}
                          className="rounded border-slate-300 dark:border-slate-600"
                        />
                      </th>
                      <th className="p-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Domain</th>
                      <th className="p-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase w-20">Visits</th>
                      <th className="p-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase w-20">Devices</th>
                      <th className="p-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Product Name</th>
                      <th className="p-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Category</th>
                      <th className="p-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase w-20">Wildcard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPending.map((row) => {
                      const s = getPendingSetting(row.domain);
                      const isSelected = selectedDomains.has(row.domain);
                      return (
                        <tr
                          key={row.domain}
                          className={clsx(
                            'border-b border-slate-100 dark:border-slate-700/50 transition-colors',
                            isSelected ? 'bg-brand-50/50 dark:bg-brand-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/30'
                          )}
                        >
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleDomain(row.domain)}
                              className="rounded border-slate-300 dark:border-slate-600"
                            />
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-sm text-slate-900 dark:text-white">{row.domain}</span>
                          </td>
                          <td className="p-3 text-right text-sm text-slate-600 dark:text-slate-400">
                            {(row.visit_count || 0).toLocaleString()}
                          </td>
                          <td className="p-3 text-right text-sm text-slate-600 dark:text-slate-400">
                            {(row.unique_devices || 0).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <input
                              type="text"
                              value={s.name}
                              onChange={(e) => updatePendingSetting(row.domain, 'name', e.target.value)}
                              placeholder={row.domain.split('.')[0]}
                              className="w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            />
                          </td>
                          <td className="p-3">
                            <select
                              value={s.categoryIds[0] || ''}
                              onChange={(e) => updatePendingSetting(row.domain, 'categoryIds', e.target.value ? [e.target.value] : [])}
                              className="w-full px-2 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                            >
                              <option value="">No Category</option>
                              {(categories || []).map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="p-3 text-center">
                            <input
                              type="checkbox"
                              checked={s.wildcard || false}
                              onChange={(e) => updatePendingSetting(row.domain, 'wildcard', e.target.checked)}
                              className="rounded border-slate-300 dark:border-slate-600"
                              title={s.wildcard ? `*.${row.domain}` : row.domain}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPendingPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    Page {pendingPage} of {totalPendingPages} ({pendingTools.length} total)
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setPendingPage(p => Math.max(1, p - 1)); setSelectedDomains(new Set()); }}
                      disabled={pendingPage === 1}
                      className="px-3 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => { setPendingPage(p => Math.min(totalPendingPages, p + 1)); setSelectedDomains(new Set()); }}
                      disabled={pendingPage === totalPendingPages}
                      className="px-3 py-1 text-sm rounded border border-slate-200 dark:border-slate-600 disabled:opacity-50 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card p-8 text-center text-slate-500 dark:text-slate-400">
              No pending products to review - all domains are categorized!
            </div>
          )}

          {/* Bulk Action Bar */}
          {selectedDomains.size > 0 && (
            <div className="sticky bottom-0 z-10 card p-4 flex items-center justify-between bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {selectedDomains.size} domain{selectedDomains.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => bulkIgnoreMutation.mutate([...selectedDomains])}
                  disabled={bulkIgnoreMutation.isPending}
                  className="px-4 py-2 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Ignore Selected
                </button>
                <button
                  onClick={() => bulkApproveMutation.mutate({ domains: [...selectedDomains], isApproved: false })}
                  disabled={bulkApproveMutation.isPending}
                  className="px-4 py-2 text-sm font-medium rounded border border-amber-300 dark:border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-50"
                >
                  Unapproved
                </button>
                <button
                  onClick={() => bulkApproveMutation.mutate({ domains: [...selectedDomains], isApproved: true })}
                  disabled={bulkApproveMutation.isPending}
                  className="px-4 py-2 text-sm font-medium rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {bulkApproveMutation.isPending ? 'Approving...' : 'Approve Selected'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ignored Domains Tab */}
      {activeTab === 'ignored' && (
        <div className="space-y-4">
          {ignoredDomains.length > 0 && (
            <div className="card p-4 bg-slate-100 dark:bg-slate-700/50 border-slate-200 dark:border-slate-600">
              <p className="text-slate-700 dark:text-slate-300 text-sm">
                <strong>Ignored Domains:</strong> {ignoredDomains.length} domains that you've chosen to ignore.
              </p>
            </div>
          )}
          <DataTable
            columns={[
              {
                key: 'domain',
                label: 'Domain',
                render: (value) => <span className="font-mono text-sm">{value}</span>,
              },
              {
                key: 'visit_count',
                label: 'Visits',
                sortable: true,
                render: (value) => (value || 0).toLocaleString(),
              },
              {
                key: 'unique_devices',
                label: 'Devices',
                sortable: true,
                render: (value) => (value || 0).toLocaleString(),
              },
              {
                key: 'first_seen_at',
                label: 'First Seen',
                render: (value) => value ? new Date(value).toLocaleDateString() : '-',
              },
              {
                key: 'actions',
                label: 'Actions',
                render: (_, row) => (
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-brand-50 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 rounded text-sm hover:bg-brand-100 dark:hover:bg-brand-500/30 font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        unignoreDomainMutation.mutate(row.domain);
                      }}
                    >
                      Move to Pending
                    </button>
                    <button
                      className="px-3 py-1 bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-400 rounded text-sm hover:bg-red-100 dark:hover:bg-red-500/30 font-medium"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const confirmed = await confirm({
                          title: 'Delete Ignored Domain',
                          message: `Are you sure you want to permanently delete ${row.domain}? This action cannot be undone.`,
                          confirmText: 'Delete',
                          type: 'danger',
                        });
                        if (confirmed) {
                          blockDomainMutation.mutate(row.domain);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ),
              },
            ]}
            data={ignoredDomains}
            emptyMessage="No ignored domains - domains you ignore will appear here."
          />
        </div>
      )}

      {/* MODALS */}

      {/* Add Tool Modal */}
      {showAddToolModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Product</h3>
            <form onSubmit={handleAddTool} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name</label>
                <input
                  type="text"
                  className="input"
                  value={newTool.name}
                  onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
                  placeholder="e.g., Canvas"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Domain Pattern</label>
                <input
                  type="text"
                  className="input"
                  value={newTool.domainPattern}
                  onChange={(e) => setNewTool({ ...newTool, domainPattern: e.target.value })}
                  placeholder="e.g., *.instructure.com"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Use * for wildcard subdomains</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categories</label>
                <div className="border border-surface-200 dark:border-surface-600 rounded-lg max-h-36 overflow-y-auto p-2 space-y-1">
                  {categories.map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-100 dark:hover:bg-surface-700 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={newTool.categoryIds.includes(String(cat.id))}
                        onChange={(e) => {
                          const id = String(cat.id);
                          setNewTool({
                            ...newTool,
                            categoryIds: e.target.checked
                              ? [...newTool.categoryIds, id]
                              : newTool.categoryIds.filter(cid => cid !== id)
                          });
                        }}
                        className="rounded border-surface-300 dark:border-surface-600"
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cat.color || '#64748b' }}
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
                {newTool.categoryIds.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{newTool.categoryIds.length} selected</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor</label>
                <input
                  type="text"
                  className="input"
                  value={newTool.vendor}
                  onChange={(e) => setNewTool({ ...newTool, vendor: e.target.value })}
                  placeholder="e.g., Instructure"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddToolModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={addToolMutation.isPending}
                >
                  {addToolMutation.isPending ? 'Adding...' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tool Modal */}
      {showEditToolModal && editTool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Product</h3>
            <form onSubmit={handleUpdateTool} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name</label>
                <input
                  type="text"
                  className="input"
                  value={editTool.name}
                  onChange={(e) => setEditTool({ ...editTool, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Domain Pattern</label>
                <input
                  type="text"
                  className="input"
                  value={editTool.domainPattern}
                  onChange={(e) => setEditTool({ ...editTool, domainPattern: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categories</label>
                <div className="border border-surface-200 dark:border-surface-600 rounded-lg max-h-36 overflow-y-auto p-2 space-y-1">
                  {categories.map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-100 dark:hover:bg-surface-700 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={(editTool.categoryIds || []).includes(String(cat.id))}
                        onChange={(e) => {
                          const id = String(cat.id);
                          setEditTool({
                            ...editTool,
                            categoryIds: e.target.checked
                              ? [...(editTool.categoryIds || []), id]
                              : (editTool.categoryIds || []).filter(cid => cid !== id)
                          });
                        }}
                        className="rounded border-surface-300 dark:border-surface-600"
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cat.color || '#64748b' }}
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
                {(editTool.categoryIds || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{editTool.categoryIds.length} selected</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor</label>
                <input
                  type="text"
                  className="input"
                  value={editTool.vendor || ''}
                  onChange={(e) => setEditTool({ ...editTool, vendor: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="input"
                  value={editTool.isApproved ? 'true' : 'false'}
                  onChange={(e) => setEditTool({ ...editTool, isApproved: e.target.value === 'true' })}
                >
                  <option value="true">Approved</option>
                  <option value="false">Unapproved</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditToolModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={updateToolMutation.isPending}
                >
                  {updateToolMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Category</h3>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  className="input"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Science"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  className="input"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="e.g., Science learning tools"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    className="w-12 h-10 rounded cursor-pointer"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input flex-1"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCategoryModal(false);
                    setNewCategory({ name: '', description: '', color: '#22c55e' });
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={createCategoryMutation.isPending}
                >
                  {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {editCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Category</h3>
            <form onSubmit={handleUpdateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  className="input"
                  value={editCategoryModal.name}
                  onChange={(e) => setEditCategoryModal({ ...editCategoryModal, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  className="input"
                  value={editCategoryModal.description || ''}
                  onChange={(e) => setEditCategoryModal({ ...editCategoryModal, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    className="w-12 h-10 rounded cursor-pointer"
                    value={editCategoryModal.color || '#22c55e'}
                    onChange={(e) => setEditCategoryModal({ ...editCategoryModal, color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input flex-1"
                    value={editCategoryModal.color || '#22c55e'}
                    onChange={(e) => setEditCategoryModal({ ...editCategoryModal, color: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditCategoryModal(null)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={updateCategoryMutation.isPending}
                >
                  {updateCategoryMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {deleteCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Delete Category</h3>
            <p className="text-slate-400 mb-4">
              Are you sure you want to delete <strong className="text-white">{deleteCategoryModal.name}</strong>?
            </p>

            {deleteCategoryModal.tool_count > 0 && (
              <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-amber-300 text-sm mb-3">
                  <strong>Warning:</strong> This category has {deleteCategoryModal.tool_count} product(s) assigned.
                  Please select a category to reassign them to:
                </p>
                <select
                  className="input w-full"
                  value={reassignCategory}
                  onChange={(e) => setReassignCategory(e.target.value)}
                  required
                >
                  <option value="">Select category...</option>
                  {categories
                    .filter(c => c.id !== deleteCategoryModal.id)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))
                  }
                </select>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDeleteCategoryModal(null);
                  setReassignCategory('');
                }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
                disabled={deleteCategoryMutation.isPending || (deleteCategoryModal.tool_count > 0 && !reassignCategory)}
              >
                {deleteCategoryMutation.isPending ? 'Deleting...' : 'Delete Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tool Modal (from pending review) */}
      {approveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">
              {approveModal.isApproved ? 'Add as Approved Product' : 'Add as Unapproved Product'}
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              Add products matching <span className="font-mono text-brand-400">{newTool.domainPattern}</span> to the inventory
              {approveModal.isApproved
                ? <span className="text-brand-400"> (Approved)</span>
                : <span className="text-amber-400"> (Unapproved)</span>
              }
            </p>
            <form onSubmit={handleApprove} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name *</label>
                <input
                  type="text"
                  className="input"
                  value={newTool.name}
                  onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
                  placeholder="e.g., Edpuzzle"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Domain Pattern *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input flex-1 font-mono text-sm"
                    value={newTool.domainPattern}
                    onChange={(e) => setNewTool({ ...newTool, domainPattern: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const domain = newTool.domainPattern || '';
                      if (!domain.startsWith('*.')) {
                        const parts = domain.split('.');
                        if (parts.length > 2) {
                          setNewTool({ ...newTool, domainPattern: `*.${parts.slice(-2).join('.')}` });
                        } else if (parts.length === 2) {
                          setNewTool({ ...newTool, domainPattern: `*.${domain}` });
                        }
                      }
                    }}
                    className="px-3 py-1 bg-surface-100 dark:bg-surface-700 hover:bg-surface-200 dark:hover:bg-surface-600 rounded text-xs font-medium transition-colors border border-surface-200 dark:border-surface-600"
                  >
                    Wildcard
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Categories</label>
                <div className="border border-surface-200 dark:border-surface-600 rounded-lg max-h-36 overflow-y-auto p-2 space-y-1">
                  {categories.map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-100 dark:hover:bg-surface-700 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={(newTool.categoryIds || []).includes(String(cat.id))}
                        onChange={(e) => {
                          const id = String(cat.id);
                          setNewTool({
                            ...newTool,
                            categoryIds: e.target.checked
                              ? [...(newTool.categoryIds || []), id]
                              : (newTool.categoryIds || []).filter(cid => cid !== id)
                          });
                        }}
                        className="rounded border-surface-300 dark:border-surface-600"
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: cat.color || '#64748b' }}
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
                {(newTool.categoryIds || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{newTool.categoryIds.length} selected</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor</label>
                <input
                  type="text"
                  className="input"
                  value={newTool.vendor}
                  onChange={(e) => setNewTool({ ...newTool, vendor: e.target.value })}
                  placeholder="e.g., Edpuzzle Inc."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setApproveModal(null);
                    setNewTool({ name: '', domainPattern: '', categoryIds: [], vendor: '' });
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={clsx(
                    "flex-1 font-medium py-2 px-4 rounded transition-colors",
                    approveModal.isApproved
                      ? "bg-brand-600 hover:bg-brand-700 text-white"
                      : "bg-amber-600 hover:bg-amber-700 text-white"
                  )}
                  disabled={approveMutation.isPending}
                >
                  {approveMutation.isPending
                    ? 'Adding...'
                    : approveModal.isApproved ? 'Add as Approved' : 'Add as Unapproved'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
